import posthog from "posthog-js";
import type { CaptureResult } from "posthog-js";
import { orpc, orpcClient, queryClient } from "@/frontend/orpc-client";

/**
 * Property keys that must never appear in PostHog events.
 * Any property whose key matches (case-insensitive) is replaced with "[redacted]".
 */
const PII_KEYS = new Set([
  "accountfhirid", "accountid", "accesstoken", "authorization", "bundle",
  "communicationemail", "cookie", "email", "externalid", "externalpatientid",
  "fhirresource", "firstname", "idtoken", "ip", "ipaddress", "key", "lastname",
  "memberid", "membershipid", "mrn", "name", "password", "patientemail",
  "patientid", "patientmembershipid", "patientname", "patientsessionid", "phone",
  "providerid", "refreshtoken", "resource", "sessionid", "ssn", "token", "userid",
]);

const EMAIL_RE = /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/g;
const MAGIC_LINK_RE = /(\/api\/auth\/magic-link\/verify\?)[^\s"'<>]*/g;
const PATIENT_SESSION_TOKEN_RE = /\/c\/[^\s"'<>?]+/g;
const IPV4_RE = /\b(?:\d{1,3}\.){3}\d{1,3}\b/g;
const PATIENT_REFERENCE_RE = /\bPatient\/[A-Za-z0-9._-]+/gi;
const BEARER_TOKEN_RE = /\b(Bearer)\s+[^\s,"'<>]+/gi;

/**
 * Query-string keys whose values identify a specific user/patient/invite and
 * must be stripped from `$current_url` / `$referrer` before PostHog stores them.
 */
const SENSITIVE_QUERY_KEYS = new Set([
  "accountid", "code", "id", "membershipid", "patientid", "patientmembershipid",
  "patientsessionid", "sessionid", "state", "token", "userid",
]);

/** Normalizes analytics property and query names for case-insensitive matching. */
function normalizedKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Returns `url` with sensitive query-param values replaced by "[redacted]".
 * Non-URL strings are passed through unchanged.
 */
export function sanitizeUrl(url: string): string {
  try {
    const u = new URL(url, "http://placeholder.invalid");
    let changed = false;
    if (u.pathname.startsWith("/c/")) {
      u.pathname = "/c/[redacted]";
      changed = true;
    }
    for (const key of [...u.searchParams.keys()]) {
      if (SENSITIVE_QUERY_KEYS.has(normalizedKey(key))) {
        u.searchParams.set(key, "[redacted]");
        changed = true;
      }
    }
    if (!changed) return url;
    return u.host === "placeholder.invalid"
      ? `${u.pathname}${u.search}${u.hash}`
      : u.toString();
  } catch {
    return url;
  }
}

/** Recursively strips PHI, PII, credentials, and sensitive URLs from properties. */
export function scrubPosthogProperties(props: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) {
    const normalized = normalizedKey(k);
    if (PII_KEYS.has(normalized) || normalized.endsWith("email") || normalized.endsWith("name") || normalized.endsWith("token")) {
      out[k] = "[redacted]";
    } else if (typeof v === "string") {
      out[k] = sanitizeUrl(v)
        .replace(EMAIL_RE, "[email]")
        .replace(MAGIC_LINK_RE, "$1[redacted]")
        .replace(PATIENT_SESSION_TOKEN_RE, "/c/[redacted]")
        .replace(IPV4_RE, "[ip]")
        .replace(PATIENT_REFERENCE_RE, "Patient/[redacted]")
        .replace(BEARER_TOKEN_RE, "$1 [redacted]");
    } else if (Array.isArray(v)) {
      out[k] = v.map((entry) =>
        entry && typeof entry === "object" && !Array.isArray(entry)
          ? scrubPosthogProperties(Object.fromEntries(Object.entries(entry)))
          : typeof entry === "string" ? sanitizeUrl(entry).replace(EMAIL_RE, "[email]") : entry,
      );
    } else if (v && typeof v === "object" && !Array.isArray(v)) {
      out[k] = scrubPosthogProperties(Object.fromEntries(Object.entries(v)));
    } else {
      out[k] = v;
    }
  }
  return out;
}

/** Redacts sensitive values from every event before it leaves the browser. */
export function redactPosthogEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return null;
  if (event.properties) event.properties = scrubPosthogProperties(event.properties);
  return event;
}

/**
 * Fetches runtime config via `orpc.runtimeConfig.get` then initializes PostHog with:
 * - Session recording with all inputs masked
 * - Patient data elements blocked via CSS selectors
 * - PII redaction on every outbound event via before_send
 *
 * No-ops silently if POSTHOG_KEY is not configured on the server.
 */
export async function initPosthog() {
  try {
    const config = await orpcClient.runtimeConfig.get({});
    // prime the tanstack query cache so RequestFacilityLink's useQuery reuses this fetch
    queryClient.setQueryData(orpc.runtimeConfig.get.key({ input: {} }), config);
    if (!config.posthogKey) return;

    posthog.init(config.posthogKey, {
      api_host: config.posthogHost || "https://us.i.posthog.com",
      capture_pageview: false,
      capture_pageleave: true,
      persistence: "localStorage+cookie",

      // Off — default autocapture serializes the text content of clicked
      // elements, which would include patient names/emails on admin screens
      // (e.g. the patient list rows in routes/dashboard/patients.tsx). We
      // emit our own `$pageview` events with sanitized URLs instead.
      autocapture: false,

      // Scrub PII from every outbound event.
      before_send: redactPosthogEvent,

      session_recording: {
        // Mask all input field values — covers email, password, text, search, tel, etc.
        maskAllInputs: true,
        maskInputOptions: {
          password: true,
          email: true,
          text: true,
          search: true,
          tel: true,
          url: true,
          number: true,
          textarea: true,
        },
        // Mask text in elements marked ph-mask or data-patient (patient names, DOBs, etc.)
        maskTextSelector: ".ph-mask,[data-ph-mask],[data-patient]",
        // Block entire elements (replaced with placeholder box) for raw patient data.
        blockSelector: ".ph-no-capture,[data-ph-no-capture]",
      },
    });
  } catch {
    // silently skip if config endpoint unavailable
  }
}

export { posthog };
